// Authentication Service
// Handles login and user data fetching from API

import { getServer } from './serverConfig';

const TOKEN_KEY = 'auth_token';
const USER_DATA_KEY = 'user_data';
const TENANT_ID = 'newa';
const API_VERSION = '2.0';

// Get stored token from localStorage
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

// Store token in localStorage
export const setToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

// Remove token from localStorage (logout)
export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_DATA_KEY);
};

// Get stored user data
export const getUserData = () => {
  const userDataStr = localStorage.getItem(USER_DATA_KEY);
  if (userDataStr) {
    try {
      return JSON.parse(userDataStr);
    } catch (e) {
      console.error('Error parsing user data:', e);
      return null;
    }
  }
  return null;
};

// The logged-in user's organisation _id (used to scope org-level APIs such as
// geo-fences). Returns null if unavailable.
export const getOrganisationId = () => {
  const userData = getUserData();
  const org = userData && userData.organisation;
  if (!org) return null;
  if (typeof org === 'string') return org;
  return org._id || org.id || null;
};

// Store user data in localStorage
export const setUserData = (userData) => {
  localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getToken();
};

// Login API call
export const login = async (email, password, organisation) => {
  try {
    const API_BASE_URL = getServer();
    console.log('Making login API call to:', `${API_BASE_URL}/auth/login`);
    console.log('Request body:', { email, organisation, password: '***' });
    
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        organisation,
      }),
    });

    console.log('Login response status:', response.status);
    console.log('Login response headers:', Object.fromEntries(response.headers.entries()));

    if (response.status === 201) {
      const data = await response.json();
      console.log('Login response data:', { ...data, accessToken: data.accessToken ? '***' : null });
      
      if (data.accessToken) {
        setToken(data.accessToken);
        console.log('Token stored successfully');
        return { success: true, token: data.accessToken };
      } else {
        console.error('No accessToken in response:', data);
        return { success: false, error: 'No access token in response', responseData: data };
      }
    } else {
      const errorText = await response.text();
      console.error('Login failed - Status:', response.status);
      console.error('Login failed - Response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { message: errorText || 'Login failed' };
      }
      
      return { 
        success: false, 
        error: errorData.message || errorData.error || `Login failed with status ${response.status}`,
        status: response.status,
        responseData: errorData
      };
    }
  } catch (error) {
    console.error('Login network error:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    return { 
      success: false, 
      error: error.message || 'Network error during login. Please check your internet connection.',
      errorType: error.name || 'NetworkError'
    };
  }
};

// Fetch user data API call
export const fetchUserData = async () => {
  const token = getToken();
  if (!token) {
    console.error('No token available for user data fetch');
    return { success: false, error: 'No token available' };
  }

  try {
    const API_BASE_URL = getServer();
    console.log('Fetching user data from:', `${API_BASE_URL}/auth/user`);
    console.log('Using token:', token.substring(0, 20) + '...');
    
    const response = await fetch(`${API_BASE_URL}/auth/user`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token, // Note: No "Bearer " prefix as per requirements
        'X-Tenant-Id': TENANT_ID,
        'X-Api-Version': API_VERSION,
      },
    });

    console.log('User data response status:', response.status);

    if (response.status === 200) {
      const userData = await response.json();
      console.log('User data received:', userData);
      setUserData(userData);
      return { success: true, userData };
    } else {
      const errorText = await response.text();
      console.error('User data fetch failed - Status:', response.status);
      console.error('User data fetch failed - Response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { message: errorText || 'Failed to fetch user data' };
      }
      
      return { 
        success: false, 
        error: errorData.message || errorData.error || `Failed to fetch user data with status ${response.status}`,
        status: response.status,
        responseData: errorData
      };
    }
  } catch (error) {
    console.error('Fetch user data network error:', error);
    return { 
      success: false, 
      error: error.message || 'Network error while fetching user data',
      errorType: error.name || 'NetworkError'
    };
  }
};

// Complete login flow: login + fetch user data
export const performLogin = async (email, password, organisation) => {
  console.log('=== Starting complete login flow ===');
  
  // Step 1: Login to get token
  console.log('Step 1: Attempting login...');
  const loginResult = await login(email, password, organisation);
  
  if (!loginResult.success) {
    console.error('Step 1 failed:', loginResult.error);
    return loginResult;
  }
  
  console.log('Step 1 successful: Token obtained');

  // Step 2: Fetch user data with token
  console.log('Step 2: Fetching user data...');
  const userDataResult = await fetchUserData();
  
  if (!userDataResult.success) {
    console.error('Step 2 failed:', userDataResult.error);
    // Token was set but user data fetch failed
    // Optionally remove token here if you want to require re-login
    return userDataResult;
  }

  console.log('Step 2 successful: User data obtained');
  console.log('=== Complete login flow successful ===');

  return { 
    success: true, 
    token: loginResult.token, 
    userData: userDataResult.userData 
  };
};

// Logout function
export const logout = () => {
  removeToken();
  // Clear reclassification cache so stale data doesn't persist across sessions
  localStorage.removeItem('reclass_results');
  // Redirect to login page
  window.location.href = '/';
};

// Organisation management
const SAVED_ORGANISATIONS_KEY = 'saved_organisations';

/**
 * Save organisation to saved organisations list
 * @param {string} slug - Organisation slug (used for login)
 * @param {string} name - Organisation name (for display)
 */
export const saveOrganisation = (slug, name) => {
  try {
    const saved = getSavedOrganisations();
    
    // Check if organisation already exists
    const existingIndex = saved.findIndex(org => org.slug === slug);
    
    if (existingIndex >= 0) {
      // Update existing organisation
      saved[existingIndex] = { slug, name, lastUsed: new Date().toISOString() };
    } else {
      // Add new organisation
      saved.push({ slug, name, lastUsed: new Date().toISOString() });
    }
    
    // Sort by last used (most recent first)
    saved.sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed));
    
    // Keep only last 10 organisations
    const limited = saved.slice(0, 10);
    
    localStorage.setItem(SAVED_ORGANISATIONS_KEY, JSON.stringify(limited));
  } catch (error) {
    console.error('Error saving organisation:', error);
  }
};

/**
 * Get saved organisations list
 * @returns {Array} Array of {slug, name, lastUsed} objects
 */
export const getSavedOrganisations = () => {
  try {
    const savedStr = localStorage.getItem(SAVED_ORGANISATIONS_KEY);
    if (savedStr) {
      return JSON.parse(savedStr);
    }
    return [];
  } catch (error) {
    console.error('Error loading saved organisations:', error);
    return [];
  }
};